import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search } from "lucide-react";
import { Nav } from "../components/Nav";
import { inputCls } from "../components/request/Field";
import { http, errMsg, statusOf, fmtDate, handoffLabel } from "../lib/api";

const FLOW = ["received", "diagnosing", "repairing", "ready", "completed"];

const Stepper = ({ status }) => {
  const idx = status === "awaiting_parts" ? 1 : FLOW.indexOf(status);
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {FLOW.map((k, i) => (
        <div key={k}>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: i * 0.1 }} style={{ originX: 0 }} className={`h-1.5 ${i <= idx ? "bg-ember" : "bg-white/10"}`} />
          <p className={`mt-2 hidden font-mono text-[11px] uppercase tracking-wider sm:block ${i <= idx ? "text-bone" : "text-slate-500"}`}>{statusOf(k).label}</p>
        </div>
      ))}
    </div>
  );
};

const Result = ({ r }) => {
  const s = statusOf(r.status);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} data-testid="track-result" className="border border-white/10 bg-slab/70 p-6 md:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-sm text-ember">{r.ref}</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold">{r.brand_model}</h2>
          <p className="mt-1 text-sm text-slate-400">Hi {r.name} · {r.device_type}{r.kind !== "build" && ` · ${handoffLabel(r.handoff)}`}</p>
        </div>
        <span data-testid="track-status-badge" className="border px-3 py-1.5 font-mono text-xs uppercase tracking-widest" style={{ borderColor: s.color, color: s.color }}>{s.label}</span>
      </div>
      {r.status !== "cancelled" && <div className="mt-10"><Stepper status={r.status} /></div>}
      {(r.public_note || r.estimate) && (
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {r.public_note && <div className="border-l-2 border-ember pl-4"><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Note from the bench</p><p className="mt-2 text-sm">{r.public_note}</p></div>}
          {r.estimate && <div className="border-l-2 border-amber-400 pl-4"><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Estimate</p><p className="mt-2 text-sm">{r.estimate}</p></div>}
        </div>
      )}
      <div className="mt-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">History</p>
        <ul className="mt-4 space-y-3">
          {[...(r.history || [])].reverse().map((h, i) => (
            <li key={i} className="flex gap-4 text-sm">
              <span className="w-28 shrink-0 font-mono text-xs text-slate-400">{fmtDate(h.at)}</span>
              <span>{statusOf(h.status).label}{h.note ? ` — ${h.note}` : ""}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

export default function Track() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.get(`/repairs/track/${encodeURIComponent(ref.trim())}`, { params: { email: email.trim() } });
      setRes(data);
    } catch (e2) {
      setRes(null);
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain grid-lines relative min-h-screen">
      <Nav />
      <main className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-24 pt-28 md:px-10 md:pt-36 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">Repair tracker</p>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">Where's my machine?</h1>
          <p className="mt-6 max-w-md text-slate-300">Enter the WT- reference from your request and the email you used.</p>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="track-form">
            <input data-testid="track-ref-input" required className={`${inputCls} font-mono uppercase`} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="WT-12345" />
            <input data-testid="track-email-input" required type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            <button data-testid="track-submit-btn" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 bg-ember px-6 py-4 font-semibold text-ink transition-colors hover:bg-amber-400 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track repair
            </button>
            {err && <p data-testid="track-error" className="text-sm text-red-400">{err}</p>}
          </form>
        </div>
        <div className="lg:col-span-7">
          {res ? <Result r={res} /> : (
            <div className="hidden h-full min-h-[360px] items-center justify-center border border-dashed border-white/10 lg:flex">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Status appears here</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
